import { publish } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';

interface OptionValueSelectionChangeEvent {
  data: {
    event: Event;
    target: HTMLElement;
    selectedOptionValues: string[];
  };
}

class VariantSelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback(): void {
    this.addEventListener('change', (event: Event) => {
      const target = this.getInputForEventTarget(event.target as HTMLElement);
      this.updateSelectionMetadata({ target });

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      } as OptionValueSelectionChangeEvent);
    });
  }

  updateSelectionMetadata({ target }: { target: HTMLElement }): void {
    if (!target) return;

    const { value, tagName } = target as HTMLSelectElement | HTMLInputElement;

    if (tagName === 'SELECT' && (target as HTMLSelectElement).selectedOptions.length) {
      const selectElement = target as HTMLSelectElement;
      const previouslySelectedOption = Array.from(selectElement.options).find((option) =>
        option?.getAttribute('selected'),
      );

      if (previouslySelectedOption) {
        previouslySelectedOption.removeAttribute('selected');
      }

      if (selectElement.selectedOptions[0]) {
        selectElement.selectedOptions[0].setAttribute('selected', 'selected');
      }

      const swatchValue = selectElement.selectedOptions[0]?.dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = selectElement
        .closest('.product-form-input')
        ?.querySelector('[data-selected-value] > .swatch') as HTMLElement;

      if (!selectedDropdownSwatchValue) return;

      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        selectElement.selectedOptions[0]?.dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && (target as HTMLInputElement).type === 'radio') {
      const inputElement = target as HTMLInputElement;
      const selectedSwatchValue = inputElement.closest(`.product-form-input`)?.querySelector('[data-selected-value]');
      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = value || '';
      }
    }
  }

  getInputForEventTarget(target: HTMLElement): HTMLElement {
    if (target.tagName === 'SELECT') {
      const selectElement = target as HTMLSelectElement;
      const selectedOption = selectElement.selectedOptions[0];
      return selectedOption || target;
    }
    return target;
  }

  get selectedOptionValues(): string[] {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .map((element) => (element as HTMLElement).dataset.optionValueId || '')
      .filter(Boolean);
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}
